import React, { useRef, useState } from "react";
import Modal from "react-modal";
import { FaLink, FaCopy, FaCheck, FaTimes } from "react-icons/fa";

const CopyLinkModal = ({ type, isOpen, onClose, link }) => {
  const [copied, setCopied] = useState(false);
  const textAreaRef = useRef(null);

  const copyToClipboard = async () => {
    try {
      if (textAreaRef.current) {
        textAreaRef.current.select();
        document.execCommand("copy");
      } else {
        await navigator.clipboard.writeText(link);
      }

      setCopied(true);

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  // Select all text when clicking on the textarea
  const handleTextAreaClick = () => {
    if (textAreaRef.current) {
      textAreaRef.current.select();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Copy Lead Link"
      className="bg-white rounded-xl shadow-2xl mx-auto relative overflow-hidden"
      style={{
        content: {
          width: "550px",
          maxWidth: "95%",
          border: "none",
          padding: 0,
        },
      }}
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[2000]"
      ariaHideApp={false}
    >
      {/* Header */}
      <div className="bg-indigo-600 text-white px-6 py-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold flex items-center">
          <FaLink className="mr-2" />
          {type === "enroll" ? "Share Enroll Link" : "Share Lead Link"}
        </h2>

        <button
          onClick={onClose}
          className="text-white hover:text-indigo-200 transition-colors focus:outline-none"
          aria-label="Close dialog"
        >
          <FaTimes className="text-xl" />
        </button>
      </div>

      {/* Body */}
      <div className="p-6">
        <p className="text-gray-600 mb-3">
          {type === "enroll"
            ? "Copy this link to share access to this form:"
            : "Copy this link to share access to this lead:"}
        </p>

        <div className="bg-gray-50 border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition-all">
          <div className="flex items-stretch">
            <textarea
              ref={textAreaRef}
              value={link}
              readOnly
              onClick={handleTextAreaClick}
              className="w-full py-3 px-4 bg-transparent border-none focus:outline-none text-gray-700 resize-none"
              style={{
                wordBreak: "break-all",
                fontSize: "14px",
                minHeight: "80px",
              }}
              aria-label="Lead link URL"
            />
          </div>
        </div>

        {/* Success message */}
        {copied && (
          <div className="mt-2 text-green-600 flex items-center text-sm">
            <FaCheck className="mr-1" />
            Link copied to clipboard!
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center"
          >
            Cancel
          </button>

          <button
            onClick={copyToClipboard}
            className={`px-5 py-2 rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center ${
              copied
                ? "bg-green-600 hover:bg-green-700"
                : "bg-indigo-600 hover:bg-indigo-700"
            } transition-colors`}
          >
            {copied ? (
              <>
                <FaCheck className="mr-2" />
                Copied!
              </>
            ) : (
              <>
                <FaCopy className="mr-2" />
                Copy Link
              </>
            )}
          </button>
        </div>
      </div>

      {/* Optional footer */}
      <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 text-xs text-gray-500">
        {type === "enroll"
          ? "Anyone with this link will be able to access enrollment form."
          : "Anyone with this link will be able to access lead information."}
      </div>
    </Modal>
  );
};

export default CopyLinkModal;
